import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf, Mail, Lock, ArrowRight, ShieldAlert } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const LoginPage = ({ setUser }) => {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [searchParams] = useSearchParams();
  const inactivity = searchParams.get("reason") === "inactivity";
  const isPatient = searchParams.get("from") === "patient";
  const logoLink = isPatient ? "/bienvenue" : "/";
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData, {
        withCredentials: true,
      });
      
      // 2FA: therapist needs to set up 2FA
      if (response.data.requires_2fa_setup) {
        navigate(`/2fa/setup?temp=${response.data.temp_token}`);
        return;
      }
      
      // 2FA: therapist needs to verify 2FA code
      if (response.data.requires_2fa) {
        navigate(`/2fa/verify?temp=${response.data.temp_token}`);
        return;
      }

      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      setUser(response.data);
      toast.success(t("auth.loginSuccess"));
      
      // Navigate based on role
      if (response.data.role === "therapist") {
        navigate("/therapist");
      } else {
        navigate("/patient");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t("auth.invalidCredentials"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API}/auth/google?redirect=/patient`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1770586968010-615ece906f63"
          alt="Calm water with lily pads"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/20" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="text-xl font-heading italic">
            "{t("auth.quote")}"
          </blockquote>
          <p className="mt-4 text-white/80">- Confucius</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to={logoLink} className="flex items-center gap-2 mb-12">
            <img src="/logo-kintsugi.png" alt="Kintsugi TCC" className="h-12 w-auto" />
            <span className="font-heading text-xl font-semibold">{t("app.name")}</span>
          </Link>

          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mb-2">{t("auth.welcome")}</h1>
          <p className="text-muted-foreground mb-8">
            {t("auth.login")}
          </p>

          {inactivity && (
            <div className="mb-6 p-3 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-lg flex items-center gap-3 text-sm text-orange-800 dark:text-orange-200" data-testid="inactivity-notice">
              <ShieldAlert className="w-5 h-5 flex-shrink-0" />
              {locale === "fr"
                ? t("auth.autoLogout")
                : "You were logged out after 15 minutes of inactivity to protect your data."}
            </div>
          )}

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            className="w-full mb-6 gap-2"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {t("auth.loginWith")}
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-background text-muted-foreground">{t("common.or")}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  className="pl-10"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                  {t("auth.forgotPasswordLink")}
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  className="pl-10"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full gap-2 bg-primary hover:bg-primary/90"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? t("auth.connecting") : t("auth.login")}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {t("auth.noAccount")}{" "}
            <Link to={isPatient ? "/register?from=patient" : "/register"} className="text-primary hover:underline" data-testid="register-link">
              {t("auth.register")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
